import { Column, Index, JoinColumn, ManyToOne } from 'typeorm';
import { Entity } from '../../decorators';
import { BaseEntity } from './base.entity';
import { CatalogValueEntity } from './catalog-value.entity';
import { ClientEntity } from './client.entity';
import { JobEntity } from './job.entity';

/**
 * PD-17448 — a customer on a job, with its role there (the tenant's Job
 * Customer Roles list: Contractor, General contractor, Owner, ...) and where
 * it stands (the Job Customer Statuses list: Bidding, Awarded, Lost, Not
 * buying). A job can belong to several customers; this replaces the project
 * participants of the removed Projects feature.
 *
 * The customers decide who may open the job (anyone who can open at least
 * one of them, owner decision 2), whose orders link to it on their own (any
 * of them, decision 8), and whose sales a reader sees in its totals (only the
 * customers the reader can open). `job.fk_client__id` stays as the job's main
 * customer, the one it was created for; the main customer always has a row
 * here too (JobCustomersService keeps them in step).
 *
 * One row per customer per job: the unique index includes removed rows, so a
 * customer taken off and added again brings its old row back, as job
 * competitors and people do.
 *
 * SM1 exposure: none (a new table; `job` is an SM2-only table as well).
 */
@Index('job_customer__job_client', ['fkJobId', 'fkClientId'], { unique: true })
@Index('job_customer__fk_client__id', ['fkClientId'], {})
@Entity('job_customer')
export class JobCustomerEntity extends BaseEntity {
	@Column('int', { name: 'fk_job__id', unsigned: true })
	public fkJobId!: number;

	@Column('int', { name: 'fk_client__id', unsigned: true })
	public fkClientId!: number;

	/** A Job Customer Roles catalog value. */
	@Column('int', { name: 'fk_catalog_value__id_role', unsigned: true, nullable: true })
	public roleId!: number | null;

	/** A Job Customer Statuses catalog value. */
	@Column('int', { name: 'fk_catalog_value__id_status', unsigned: true, nullable: true })
	public statusId!: number | null;

	@ManyToOne(() => JobEntity, { onDelete: 'CASCADE', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_job__id', referencedColumnName: 'id' }])
	public job!: JobEntity;

	@ManyToOne(() => ClientEntity, { onDelete: 'RESTRICT', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_client__id', referencedColumnName: 'id' }])
	public client!: ClientEntity;

	@ManyToOne(() => CatalogValueEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_catalog_value__id_role', referencedColumnName: 'id' }])
	public role!: CatalogValueEntity | null;

	@ManyToOne(() => CatalogValueEntity, { onDelete: 'SET NULL', onUpdate: 'RESTRICT' })
	@JoinColumn([{ name: 'fk_catalog_value__id_status', referencedColumnName: 'id' }])
	public status!: CatalogValueEntity | null;
}
